import clsx from 'clsx'
import includes from 'lodash/includes'
import React, { FC, useContext, useMemo } from 'react'

import { BlockContainer } from '@Components/BlockContainer'
import { Title } from '@Components/CMS/Title'
import { Block, PagePresentation } from '@Generated/graphql'
import { BlockContext } from '@Globals/CMS/Context'
import { getJSONFieldValue } from '@Globals/CMS/FieldHelpers'
import { MetadataContext } from '@Globals/Metadata'

import styles from './TitleBlock.module.scss'

type TitleType = 'H1' | 'H2' | 'H3'

interface TitleBlockData {
  mainTitle: string
  subTitle: string
  type: TitleType
}

const getBlockData = (titleBlock: Block): TitleBlockData => ({
  mainTitle: getJSONFieldValue(titleBlock.fields || [], 'mainTitle') || '',
  subTitle: getJSONFieldValue(titleBlock.fields || [], 'subTitle') || '',
  type: getJSONFieldValue(titleBlock.fields || [], 'type') || 'H1',
})

interface TitleBlockProps {
  data: Block
  id?: string
}

const TitleBlock: FC<TitleBlockProps> = ({
  data,
  id,
}) => {
  const { presentation } = React.use(BlockContext)
  const { appearance, common } = data
  const { page } = useContext(MetadataContext)
  const {
    mainTitle,
    subTitle,
    type,
  } = useMemo(() => getBlockData(data), [data])

  if (presentation === PagePresentation.Sidebar) {
    const fullWidth = [PagePresentation.Modal, PagePresentation.Sidebar].includes(presentation)
    return (
      <BlockContainer
        appearance={appearance || {}}
        common={common || {}}
        id={id}
        noPadding={fullWidth}
        presentation={presentation}
      >
        <Title
          className={styles.v2}
          subtitle={subTitle}
          title={mainTitle}
          type={type?.toLowerCase() as Lowercase<TitleType>}
        />
      </BlockContainer>
    )
  }

  return (
    <div
      className={clsx(
        'titleblock',
        styles.titleContainer,
        includes(['frontpages', 'category', 'filters'], page?.type) && styles.fullWidth,
        includes(['category', 'filters'], page?.type) && styles.leftAlign,
      )}
      id={id}
    >
      <Title
        subtitle={subTitle}
        title={mainTitle}
        type={type?.toLowerCase() as Lowercase<TitleType>}
      />
    </div>
  )
}

export default TitleBlock
